import { CHAT_CHANNELS, MAX_LOOKUP_PREFIX_CHARS, MAX_NAME_MATCHES, MIN_LOOKUP_CHARS, fullNameKey, lookupPrefix, matchesPrefix } from "odyssey-shared";
import { readChatInput } from "./command.ts";
import { commandNamed, listedCommands, optionSyntax, type ChatCommand, type PartyStanding } from "./commands.ts";
import type { NameMatch } from "./lookup.ts";
import { commandDescription, groupHeading } from "./words.ts";

/**
 * The stepper (ADR-068's amendment "chat commands, Discord style", item 2), pure: what the chat input shows for a
 * draft, and how its keys move through that. A draft of `/` and a partial word lists the open commands its word
 * starts; an open command's word and a space step through its options, a name taking two words, a channel, a
 * command or a duration one, and text or a family the rest of the line. A name option suggests the players in view,
 * then names a lookup found; `/invite` only the names the gateway's invite lookup answered, which already leaves out
 * whoever the player may not invite; `/block` the families in view; `/unblock` the block list; a channel option the channels
 * its typed part starts (ADR-068's amendment "Global, Trade and Help"); a reply with several recent whisperers
 * and nothing typed after it, a picker of them. Enter chooses only where the page would refuse the draft as typed,
 * so a whole command typed and Enter sends as it always did. Escape's dismissal holds until the draft changes.
 */

/**
 * What the stepper reads beside the draft: the players in view and the player's own name (the page's own names),
 * the names the latest lookup answered, the recent whisperers newest first, the block list, and where the player
 * stands in a party, which decides which commands the `/` list offers.
 */
export interface StepContext {
  readonly inView: readonly NameMatch[];
  readonly self: NameMatch | null;
  readonly lookedUp: readonly NameMatch[];
  readonly invitable: readonly NameMatch[];
  readonly whisperers: readonly NameMatch[];
  readonly blocks: readonly string[];
  readonly party: PartyStanding;
}

/**
 * One entry of a list: its key, what it shows (its label, the syntax a command takes, and a command's
 * description), the draft choosing it writes, and for a command the heading of its group when it is the first of
 * that group in the list.
 */
export interface ChatSuggestion {
  readonly id: string;
  readonly label: string;
  readonly detail: string;
  readonly description: string;
  readonly draft: string;
  readonly heading?: string;
}

/**
 * What a list offers: commands, names, families, or recent whisperers.
 */
export type ListKind = "commands" | "names" | "families" | "whisperers" | "channels";

/**
 * What the input shows for a draft: the open command whose options are being stepped and the current option's
 * place (-1 for none), a reserved command the draft names, the list, the prefix a lookup is wanted for, and for a
 * reply the newest whisperer it reaches.
 */
export interface StepView {
  readonly command: ChatCommand | null;
  readonly option: number;
  readonly reserved: ChatCommand | null;
  readonly list: { readonly kind: ListKind; readonly entries: readonly ChatSuggestion[] } | null;
  readonly lookup: string | null;
  readonly replyTo: NameMatch | null;
}

/**
 * A draft with nothing to show.
 */
const NOTHING: StepView = { command: null, option: -1, reserved: null, list: null, lookup: null, replyTo: null };

/**
 * A command's word and what follows it: leading white space, the slash and the word, the white space after it,
 * and the rest.
 */
const COMMAND = /^(\s*\/)(\S*)(\s*)([\s\S]*)$/;

/**
 * A word and the white space after it at the start of a text.
 */
const WORD = /^(\S+)(\s+)/;

/**
 * How many words each option kind takes before the next option starts; the rest of the line has no end.
 */
const WORDS_TAKEN: Readonly<Record<string, number>> = { name: 2, channel: 1, command: 1, duration: 1 };

/**
 * A name as its full name.
 *
 * @param name The name.
 * @returns The first and family names joined by one space.
 */
function full(name: NameMatch): string {
  return `${name.firstName} ${name.familyName}`;
}

/**
 * Names in the order of their full names ignoring case.
 *
 * @param names The names.
 * @returns A sorted copy.
 */
function byFullName(names: readonly NameMatch[]): NameMatch[] {
  return [...names].sort((a, b) => (fullNameKey(a.firstName, a.familyName) < fullNameKey(b.firstName, b.familyName) ? -1 : 1));
}

/**
 * The list of the open commands that apply to the player for a partial word, grouped and sorted
 * (`listedCommands`); the first entry of each group carries the group's heading.
 *
 * @param lead The draft's white space and slash before the word.
 * @param partial The word typed so far.
 * @param standing The player's standing in a party.
 * @returns The list, or null when no command applies whose word starts with it.
 */
function commandList(lead: string, partial: string, standing: PartyStanding): StepView["list"] {
  const entries = listedCommands(partial, standing).map(({ command, group }, index, all) => {
    const entry: ChatSuggestion = { id: `command:${command.id}`, label: `/${command.words[0] ?? ""}`, detail: optionSyntax(command), description: commandDescription(command), draft: `${lead.trimStart()}${command.words[0] ?? ""} ` };
    return all[index - 1]?.group === group ? entry : { ...entry, heading: groupHeading(group) };
  });
  return entries.length === 0 ? null : { kind: "commands", entries };
}

/**
 * Where the options of a command stand in the text after its word: the current option's place, the text before
 * it and the text typed of it.
 *
 * @param command The command.
 * @param rest The text after the command's word and its white space.
 * @returns The current option's place (-1 when the command takes none), the text before it, and its partial text.
 */
function currentOption(command: ChatCommand, rest: string): { readonly option: number; readonly before: string; readonly partial: string } {
  let consumed = 0;
  for (const [index, option] of command.options.entries()) {
    const taken = option.words ?? WORDS_TAKEN[option.kind];
    if (taken === undefined || index === command.options.length - 1) {
      return { option: index, before: rest.slice(0, consumed), partial: rest.slice(consumed) };
    }
    let at = consumed;
    let words = 0;
    for (; words < taken; words += 1) {
      const word = WORD.exec(rest.slice(at));
      if (word === null) {
        break;
      }
      at += word[0].length;
    }
    if (words < taken) {
      return { option: index, before: rest.slice(0, consumed), partial: rest.slice(consumed) };
    }
    consumed = at;
  }
  return { option: -1, before: rest, partial: "" };
}

/**
 * The names a name option suggests: the players in view whose full name starts with what is typed, the player's
 * own left out, in the order of the full name, then the names a lookup found that are not in view, at most
 * `MAX_NAME_MATCHES`; and the prefix a lookup is wanted for, when 2 characters or more are typed and the names in
 * view leave room, and never when the prefix is longer than `MAX_LOOKUP_PREFIX_CHARS`, since no full name is longer
 * and the lookup's schema would refuse it.
 *
 * @param partial The option's text as typed.
 * @param context What the stepper reads.
 * @returns The names and the lookup wanted.
 */
function suggestedNames(partial: string, context: StepContext): { readonly names: NameMatch[]; readonly lookup: string | null } {
  const self = context.self === null ? null : fullNameKey(context.self.firstName, context.self.familyName);
  const seen = new Set<string>(self === null ? [] : [self]);
  const inView = byFullName(context.inView).filter((name) => {
    const key = fullNameKey(name.firstName, name.familyName);
    if (seen.has(key) || !matchesPrefix(name.firstName, name.familyName, partial)) {
      return false;
    }
    seen.add(key);
    return true;
  });
  const looked = context.lookedUp.filter((name) => {
    const key = fullNameKey(name.firstName, name.familyName);
    if (seen.has(key) || !matchesPrefix(name.firstName, name.familyName, partial)) {
      return false;
    }
    seen.add(key);
    return true;
  });
  const prefix = lookupPrefix(partial);
  const lookup = prefix.length >= MIN_LOOKUP_CHARS && prefix.length <= MAX_LOOKUP_PREFIX_CHARS && inView.length < MAX_NAME_MATCHES ? prefix : null;
  return { names: [...inView, ...looked].slice(0, MAX_NAME_MATCHES), lookup };
}

/**
 * The names `/invite` suggests: the names the gateway's invite lookup answered whose full name starts with what is
 * typed, in the order of the full name, at most `MAX_NAME_MATCHES`, and the prefix a lookup is wanted for, when 2
 * characters or more are typed and not more than `MAX_LOOKUP_PREFIX_CHARS`; more than the two words of a name offer
 * and want nothing. The players in view are not read: the page
 * cannot know who among them may be invited, so nothing is offered that the gateway has not answered.
 *
 * @param partial The option's text as typed.
 * @param context What the stepper reads.
 * @returns The names and the lookup wanted.
 */
function suggestedInvitees(partial: string, context: StepContext): { readonly names: NameMatch[]; readonly lookup: string | null } {
  if (partial.trim().split(/\s+/).length > WORDS_TAKEN["name"]!) {
    return { names: [], lookup: null };
  }
  const seen = new Set<string>();
  const names = byFullName(context.invitable).filter((name) => {
    const key = fullNameKey(name.firstName, name.familyName);
    if (seen.has(key) || !matchesPrefix(name.firstName, name.familyName, partial)) {
      return false;
    }
    seen.add(key);
    return true;
  });
  const prefix = lookupPrefix(partial);
  return { names: names.slice(0, MAX_NAME_MATCHES), lookup: prefix.length >= MIN_LOOKUP_CHARS && prefix.length <= MAX_LOOKUP_PREFIX_CHARS ? prefix : null };
}

/**
 * The families an option of `/block` or `/unblock` suggests: for a block, the families of the players in view but
 * the player's own; for an unblock, the block list; each starting with what is typed, without regard to case.
 *
 * @param command The command.
 * @param partial The option's text as typed.
 * @param context What the stepper reads.
 * @returns The families, sorted for a block and in the list's order for an unblock.
 */
function suggestedFamilies(command: ChatCommand, partial: string, context: StepContext): string[] {
  const typed = partial.trim().toLowerCase();
  const own = context.self?.familyName.toLowerCase();
  const families = command.id === "unblock" ? context.blocks : [...new Set(context.inView.map((name) => name.familyName))].filter((family) => family.toLowerCase() !== own).sort((a, b) => (a.toLowerCase() < b.toLowerCase() ? -1 : 1));
  return families.filter((family) => family !== "" && family.toLowerCase().startsWith(typed));
}

/**
 * A list of names or families whose entries write their words and a space after the text before them.
 *
 * @param kind The list's kind.
 * @param words The entries' words.
 * @param head The draft up to the option.
 * @returns The list, or null when it holds nothing.
 */
function wordList(kind: ListKind, words: readonly string[], head: string): StepView["list"] {
  const entries = words.map((word) => ({ id: `${kind}:${word.toLowerCase()}`, label: word, detail: "", description: "", draft: `${head}${word} ` }));
  return entries.length === 0 ? null : { kind, entries };
}

/**
 * What the input shows for a draft.
 *
 * @param draft The input's text.
 * @param context What the stepper reads beside it.
 * @returns The view.
 * @example
 * stepChat("/w Mi", context).list?.entries[0]?.draft;
 * // "/w Mira Thornwood "
 */
export function stepChat(draft: string, context: StepContext): StepView {
  const parts = COMMAND.exec(draft);
  if (parts === null) {
    return NOTHING;
  }
  const lead = parts[1] ?? "/";
  const word = parts[2] ?? "";
  const space = parts[3] ?? "";
  const named = commandNamed(word);
  const reserved = named !== undefined && !named.open ? named : null;
  if (space === "") {
    return { ...NOTHING, reserved, list: commandList(lead, word, context.party) };
  }
  if (named === undefined || reserved !== null) {
    return { ...NOTHING, reserved };
  }
  const { option, before, partial } = currentOption(named, parts[4] ?? "");
  const head = `${lead.trimStart()}${word} ${before.replace(/\s+/g, " ")}`;
  const replyTo = named.id === "reply" ? (context.whisperers[0] ?? null) : null;
  const view: StepView = { ...NOTHING, command: named, option, replyTo };
  const kind = named.options[option]?.kind;
  if (kind === "name") {
    const { names, lookup } = named.id === "invite" ? suggestedInvitees(partial, context) : suggestedNames(partial, context);
    return { ...view, lookup, list: wordList("names", names.map(full), head) };
  }
  if (kind === "family") {
    return { ...view, list: wordList("families", suggestedFamilies(named, partial, context), head) };
  }
  if (kind === "channel") {
    const typed = partial.trim().toLowerCase();
    return { ...view, list: wordList("channels", CHAT_CHANNELS.filter((channel) => channel.startsWith(typed) && channel !== typed), head) };
  }
  if (named.id === "reply" && partial.trim() === "" && context.whisperers.length >= 2) {
    const entries = context.whisperers.map((name) => ({ id: `whisperers:${fullNameKey(name.firstName, name.familyName)}`, label: full(name), detail: "", description: "", draft: `/w ${full(name)} ` }));
    return { ...view, list: { kind: "whisperers", entries } };
  }
  return view;
}

/**
 * Where the stepper's keys stand: the highlighted entry, the draft whose list Escape dismissed (null for none), and
 * the key of the list the highlight belongs to.
 */
export interface StepperState {
  readonly highlight: number;
  readonly dismissed: string | null;
  readonly listKey: string;
}

/**
 * The stepper before any list.
 */
export const STEPPER_START: StepperState = { highlight: 0, dismissed: null, listKey: "" };

/**
 * What a key does: passes to the input and the page as it would without a list, moves the highlight, chooses an
 * entry and writes its draft, or sends.
 */
export type StepperOutcome = { readonly kind: "pass" } | { readonly kind: "move"; readonly state: StepperState } | { readonly kind: "choose"; readonly draft: string; readonly state: StepperState } | { readonly kind: "send" };

/**
 * The entries a list shows: the view's, unless Escape dismissed the list for this very draft.
 *
 * @param state Where the stepper stands.
 * @param draft The input's text.
 * @param view The view of the draft.
 * @returns The entries, empty when no list shows.
 */
export function shownEntries(state: StepperState, draft: string, view: StepView): readonly ChatSuggestion[] {
  return view.list === null || state.dismissed === draft ? [] : view.list.entries;
}

/**
 * The key of a list: its entries' ids in order.
 *
 * @param entries The entries.
 * @returns The key.
 */
function keyOf(entries: readonly ChatSuggestion[]): string {
  return entries.map((entry) => entry.id).join("\n");
}

/**
 * Brings the stepper in step with the list the draft shows now: the highlight returns to the first entry whenever
 * the list's entries change.
 *
 * @param state Where the stepper stands.
 * @param draft The input's text.
 * @param view The view of the draft.
 * @returns The state.
 */
export function syncStepper(state: StepperState, draft: string, view: StepView): StepperState {
  const listKey = keyOf(shownEntries(state, draft, view));
  return listKey === state.listKey ? state : { ...state, highlight: 0, listKey };
}

/**
 * Chooses an entry, as Tab, Enter or a click does.
 *
 * @param state Where the stepper stands.
 * @param entry The entry.
 * @returns The draft the entry writes and the stepper's state after it.
 */
export function chooseEntry(state: StepperState, entry: ChatSuggestion): { readonly draft: string; readonly state: StepperState } {
  return { draft: entry.draft, state: { ...state, highlight: 0, dismissed: null, listKey: "" } };
}

/**
 * Dismisses the list for the draft, as Escape does; it shows again once the draft changes.
 *
 * @param state Where the stepper stands.
 * @param draft The input's text.
 * @returns The state.
 */
export function dismissList(state: StepperState, draft: string): StepperState {
  return { ...state, dismissed: draft };
}

/**
 * Follows the player's edit of the draft: a dismissal ends once the draft differs from the one Escape was pressed
 * on, so typing and deleting back to that draft shows its list again.
 *
 * @param state Where the stepper stands.
 * @param draft The input's text after the edit.
 * @returns The state, the same object when nothing changes.
 */
export function editDraft(state: StepperState, draft: string): StepperState {
  return state.dismissed === null || state.dismissed === draft ? state : { ...state, dismissed: null };
}

/**
 * What a key does in the input.
 *
 * @param state Where the stepper stands.
 * @param key The key: Down, Up, Tab or Enter.
 * @param draft The input's text.
 * @param view The view of the draft.
 * @returns What it does.
 */
export function stepperKey(state: StepperState, key: "ArrowDown" | "ArrowUp" | "Tab" | "Enter", draft: string, view: StepView): StepperOutcome {
  const entries = shownEntries(state, draft, view);
  const synced = syncStepper(state, draft, view);
  const chosen = entries[Math.min(synced.highlight, entries.length - 1)];
  if (key === "Enter") {
    return chosen !== undefined && readChatInput(draft).kind === "refused" ? { kind: "choose", ...chooseEntry(synced, chosen) } : { kind: "send" };
  }
  if (chosen === undefined) {
    return { kind: "pass" };
  }
  if (key === "Tab") {
    return { kind: "choose", ...chooseEntry(synced, chosen) };
  }
  const step = key === "ArrowDown" ? 1 : entries.length - 1;
  return { kind: "move", state: { ...synced, highlight: (synced.highlight + step) % entries.length } };
}
